import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { FileText, Table, LayoutGrid, Calendar, ClipboardList, BarChart3, Ticket, ExternalLink } from "lucide-react";

const FORMATS = [
  {
    id: "mixed",
    icon: LayoutGrid,
    label: "Mixed page",
    desc: "Notion-style — combine any number of blocks (text, sheet, link, files, images, videos, etc.). Use this for everything.",
  },
  {
    id: "link",
    icon: ExternalLink,
    label: "External link",
    desc: "A clickable shortcut. Tapping the page opens an external URL (Google Sheets, Drive, Figma, anything) in a new tab.",
  },
];

export default function PageFormatDialog({ open, onOpenChange, onCreate }) {
  const [title, setTitle] = useState("");
  const [format, setFormat] = useState("mixed");
  const [externalUrl, setExternalUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setTitle(""); setFormat("mixed"); setExternalUrl(""); };

  const submit = async () => {
    if (!title.trim()) return;
    if (format === "link" && !externalUrl.trim()) return;
    setBusy(true);
    try {
      await onCreate(title.trim(), format, format === "link" ? externalUrl.trim() : null);
      reset();
      onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#111] border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-[18px] tracking-tight">Add a new page</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Page title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Content Calendar"
              autoFocus className="bg-[#0a0a0a] border-zinc-800 mt-1.5 h-10 text-[13px]" data-testid="page-title-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2 block">Page format</Label>
            <div className="space-y-2">
              {FORMATS.map(({ id, icon: Icon, label, desc }) => (
                <button key={id} onClick={() => setFormat(id)} type="button"
                  data-testid={`page-format-${id}`}
                  className={`w-full text-left flex items-start gap-3 p-3 rounded-md border transition-all ${
                    format === id
                      ? "border-white bg-white/5"
                      : "border-zinc-800 hover:border-zinc-600 bg-zinc-900/30"
                  }`}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                    format === id ? "bg-white text-black" : "bg-zinc-900 border border-zinc-800 text-zinc-300"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-[13px] font-semibold text-zinc-100">{label}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          {format === "link" && (
            <div data-testid="page-link-url-row">
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">External URL</Label>
              <Input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="bg-[#0a0a0a] border-zinc-800 mt-1.5 h-10 text-[13px] font-mono"
                data-testid="page-external-url-input" />
              <div className="text-[10px] text-zinc-600 mt-1.5">Will open in a new tab when clicked.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-500 text-[12px]">Cancel</Button>
          <Button onClick={submit} disabled={!title.trim() || (format === "link" && !externalUrl.trim()) || busy}
            className="bg-white hover:bg-zinc-200 text-black text-[13px] h-10" data-testid="page-create-submit">
            {busy ? "Creating…" : "Create page"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
